import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { API_BASE_URL } from "../lib/apiClient";
import {
  initialSessionRefetchSignal,
  initialSubscriptionState,
  reduceFleet,
  reduceSessionRefetch,
  reduceSubscription,
  type FleetFrame,
  type FleetState,
  type SessionFrame,
  type SessionRefetchSignal,
  type SnapshotFrame,
  type SubscribeCommand,
  type SubscriptionControlFrame,
  type SubscriptionState,
  type TelemetryFrame,
} from "../lib/liveFrames";

/**
 * The app's single connection to `/ws/dashboard`.
 *
 * One socket for the whole app, not one per page: the server sends a full
 * snapshot and resolves authorization on every connect, so a socket per
 * component would mean N snapshots and N authorization passes for one user.
 * Pages read this context instead.
 *
 * The frame merge rules are pure and live in `lib/liveFrames.ts`; this file is
 * only the I/O around them — connect, authenticate, reconnect, fan out.
 *
 * Auth: a browser can't set headers on a WebSocket, so the Auth0 JWT rides as a
 * `token` query param. The server caps the socket's lifetime at the token's
 * expiry and closes when it lapses; the reconnect below then opens a new one
 * with a fresh token, and the server's connect-snapshot heals whatever was
 * missed. That means an expiry close is ordinary, not an error path.
 */

interface DashboardSocket {
  /** Live fleet state by device_id: snapshot ⊕ robot_state deltas. */
  fleet: FleetState;
  /** Whether the socket is open right now. */
  connected: boolean;
  /** True once a snapshot has arrived — until then `fleet` is empty because
   *  nothing has been received, which is not the same as an empty fleet. */
  primed: boolean;
  /** Bumps whenever a `session` delta lands for a known device. */
  sessionRefetch: SessionRefetchSignal;
  /** Latest telemetry frame + a monotone seq, relayed for whichever view is
   *  subscribed. The socket holds no path — it has no idea which session is
   *  open — so accumulation belongs to `useLiveTelemetryPath`. */
  telemetry: { latest: TelemetryFrame | null; seq: number };
  /** Outcome of the last subscribe/unsubscribe. */
  subscription: SubscriptionState;
  /** Send a subscribe/unsubscribe command. No-op while the socket is closed. */
  sendCommand: (cmd: SubscribeCommand) => void;
}

const initialTelemetry = { latest: null as TelemetryFrame | null, seq: 0 };

/**
 * Three contexts, not one, because the three change at very different rates.
 * `robot_state` and `telemetry` frames both arrive at roughly 1 Hz per device,
 * so a single context value would re-render every consuming page on every
 * frame — the Sessions list repainting because an unrelated mower moved. Split
 * this way, each page only re-renders for the traffic it actually reads.
 */
const FleetCtx = createContext<FleetState>({});
const TelemetryCtx = createContext<DashboardSocket["telemetry"]>(initialTelemetry);
const SocketCtx = createContext<
  Omit<DashboardSocket, "fleet" | "telemetry">
>({
  connected: false,
  primed: false,
  sessionRefetch: initialSessionRefetchSignal,
  subscription: initialSubscriptionState,
  sendCommand: () => {},
});

/** Connection state, the session-refetch signal, and the subscribe command. */
export const useDashboardSocket = () => useContext(SocketCtx);
/** Live fleet state — only re-renders on snapshot/robot_state traffic. */
export const useLiveFleet = () => useContext(FleetCtx);
/** The raw telemetry relay — only for the view accumulating a live path. */
export const useLiveTelemetry = () => useContext(TelemetryCtx);

const MAX_BACKOFF_MS = 30_000;
const CONTROL_FRAME_TYPES = new Set(["subscribed", "error"]);

function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, "ws");
}

export function DashboardSocketProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [fleet, setFleet] = useState<FleetState>({});
  const [connected, setConnected] = useState(false);
  const [primed, setPrimed] = useState(false);
  const [sessionRefetch, setSessionRefetch] = useState(
    initialSessionRefetchSignal,
  );
  const [telemetry, setTelemetry] = useState(initialTelemetry);
  const [subscription, setSubscription] = useState(initialSubscriptionState);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  // The fleet's device set, captured synchronously from the snapshot frame. A
  // `session` delta arriving in the same tick must see it, which it wouldn't if
  // this were read out of `fleet` state.
  const knownDevicesRef = useRef<ReadonlySet<string>>(new Set());
  // Devices a view asked to subscribe to. Replayed after a reconnect, since the
  // server forgets subscriptions when the socket dies.
  const subscribedRef = useRef<Set<string>>(new Set());

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    let token: string;
    try {
      token = await getAccessTokenSilently();
    } catch {
      return; // No token — the reconnect timer or a re-auth will retry.
    }
    if (!mountedRef.current) return;

    const ws = new WebSocket(
      `${toWsUrl(API_BASE_URL)}/ws/dashboard?token=${encodeURIComponent(token)}`,
    );
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      setConnected(true);
      reconnectAttempt.current = 0;
      // Re-assert any live subscription: the new socket starts with none, and
      // the view that wanted it may never re-render to ask again.
      if (subscribedRef.current.size > 0) {
        ws.send(
          JSON.stringify({
            action: "subscribe",
            devices: Array.from(subscribedRef.current),
          } as SubscribeCommand),
        );
      }
    };

    ws.onmessage = (evt) => {
      if (!mountedRef.current) return;
      try {
        const frame = JSON.parse(evt.data) as { type?: string };

        if (frame.type && CONTROL_FRAME_TYPES.has(frame.type)) {
          setSubscription((prev) =>
            reduceSubscription(prev, frame as SubscriptionControlFrame),
          );
          return;
        }
        if (frame.type === "session") {
          setSessionRefetch((prev) =>
            reduceSessionRefetch(
              prev,
              frame as SessionFrame,
              knownDevicesRef.current,
            ),
          );
          return;
        }
        if (frame.type === "telemetry") {
          setTelemetry((prev) => ({
            latest: frame as TelemetryFrame,
            seq: prev.seq + 1,
          }));
          return;
        }
        if (frame.type === "notification") {
          return; // Command has no in-app alert surface.
        }
        if (frame.type === "snapshot") {
          knownDevicesRef.current = new Set(
            Object.keys((frame as unknown as SnapshotFrame).data ?? {}),
          );
          setPrimed(true);
        }
        setFleet((prev) => reduceFleet(prev, frame as unknown as FleetFrame));
      } catch {
        // A malformed frame is dropped: live data is best-effort and the next
        // frame (or the reconnect snapshot) supersedes it anyway.
      }
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      setConnected(false);
      wsRef.current = null;
      // Capped exponential backoff. The server re-snapshots on connect, so
      // whatever was missed during the gap is repaired by reconnecting.
      const delay = Math.min(
        1000 * 2 ** reconnectAttempt.current,
        MAX_BACKOFF_MS,
      );
      reconnectAttempt.current += 1;
      reconnectTimer.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delay);
    };

    ws.onerror = () => {
      // onclose follows and drives the reconnect; nothing to do here.
    };
  }, [getAccessTokenSilently]);

  // Stable identity (no deps) so a subscribing effect never re-runs because of
  // our re-renders; the current socket is read through the ref.
  const sendCommand = useCallback((cmd: SubscribeCommand) => {
    for (const d of cmd.devices) {
      if (cmd.action === "subscribe") subscribedRef.current.add(d);
      else subscribedRef.current.delete(d);
    }
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(cmd));
    }
    // Dropped while closed — onopen replays subscribedRef, so intent survives.
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (isAuthenticated) connect();
    return () => {
      mountedRef.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [isAuthenticated, connect]);

  const socketValue = useMemo(
    () => ({ connected, primed, sessionRefetch, subscription, sendCommand }),
    [connected, primed, sessionRefetch, subscription, sendCommand],
  );

  return (
    <SocketCtx.Provider value={socketValue}>
      <FleetCtx.Provider value={fleet}>
        <TelemetryCtx.Provider value={telemetry}>
          {children}
        </TelemetryCtx.Provider>
      </FleetCtx.Provider>
    </SocketCtx.Provider>
  );
}
